import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import { toast } from "sonner";
import { GoogleLogo, EnvelopeSimple, Lock } from "@phosphor-icons/react";
import api from "../lib/api";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { loginEmail } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await loginEmail(email, password);
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    try {
      const { data } = await api.get("/auth/google/config");
      if (!data?.enabled || !data?.client_id) {
        toast.error("Google sign-in is temporarily unavailable. Please use email and password.");
        return;
      }
      const redirectUri = window.location.origin + "/auth/google/callback";
      const params = new URLSearchParams({
        client_id: data.client_id,
        redirect_uri: redirectUri,
        response_type: "code",
        scope: "openid email profile",
        access_type: "online",
        prompt: "select_account",
      });
      window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
    } catch (e) {
      toast.error("Could not start Google sign-in. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header minimal />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 hidden lg:block border border-[#e4e4e7] bg-[#f7f7f8] p-12">
          <div className="pg-overline mb-6">Welcome back</div>
          <h2 className="font-heading text-5xl font-black tracking-tighter leading-[0.95]">Your tracked purchases<br/>are waiting<span className="text-[#002FA7]">.</span></h2>
          <p className="text-[#52525b] mt-6 max-w-md">Pick up where you left off and check for fresh price drops across your watchlist.</p>
        </div>
        <div className="lg:col-span-5">
          <div className="pg-overline mb-4">Login</div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight mb-8">Sign in to PriceGuard</h1>

          <button onClick={handleGoogle} className="w-full border border-black font-bold px-6 py-3 inline-flex items-center justify-center gap-3 hover:bg-black hover:text-white transition-colors" data-testid="login-google-btn">
            <GoogleLogo size={20} weight="bold" /> Continue with Google
          </button>

          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-[#e4e4e7]" />
            <span className="pg-overline">or</span>
            <div className="flex-1 h-px bg-[#e4e4e7]" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <label className="block">
              <span className="pg-overline">Email</span>
              <div className="mt-2 flex items-center border border-[#e4e4e7] focus-within:border-[#002FA7]">
                <EnvelopeSimple size={18} className="mx-3 text-[#52525b]" />
                <input type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} className="flex-1 py-3 pr-3 outline-none bg-white text-sm" placeholder="you@example.co.nz" data-testid="login-email-input" />
              </div>
            </label>
            <label className="block">
              <span className="pg-overline">Password</span>
              <div className="mt-2 flex items-center border border-[#e4e4e7] focus-within:border-[#002FA7]">
                <Lock size={18} className="mx-3 text-[#52525b]" />
                <input type="password" required value={password} onChange={(e)=>setPassword(e.target.value)} className="flex-1 py-3 pr-3 outline-none bg-white text-sm" placeholder="••••••••" data-testid="login-password-input" />
              </div>
            </label>
            <button type="submit" disabled={busy} className="w-full bg-[#002FA7] text-white font-bold px-6 py-3 hover:bg-[#00207A] disabled:opacity-60" data-testid="login-submit-btn">
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="mt-4 text-sm">
            <Link to="/forgot-password" className="text-[#002FA7] font-semibold hover:underline" data-testid="login-forgot-link">
              Forgot password?
            </Link>
          </div>

          <p className="mt-6 text-sm text-[#52525b]">No account yet? <Link to="/signup" className="font-bold text-black underline" data-testid="login-to-signup">Create one</Link>.</p>
        </div>
      </div>
    </div>
  );
}
